import { ReactNode } from 'react';
import { StatCardRow } from '../organisms';

/**
 * EntityHeader — composed header block for entity-detail pages.
 *
 * Composed pattern (not a single M3 component) per Agent Protocol step 2:
 *   - Composition: optional prefix code chip + title + status pills +
 *     right-aligned action buttons + optional KPI row underneath
 *   - Pattern reference: M3 "Region with supporting info" + Card
 *     Outlined for the surface
 *
 * Populus adaptations applied:
 *   - Single component takes {prefix, title, statusPills, actions, kpis}
 *     props (matches Christophe's client/property/parcel detail screen
 *     header layouts)
 *   - Prefix code chip → high-density entity identifier (e.g., "Parcel
 *     1005301") in monospace; M3 has no equivalent but business-app
 *     density demands it
 *   - Title in Display Small (M3 Display Small spec)
 *   - Right-aligned action cluster (M3 right-aligned actions per
 *     entity-detail conventions)
 *   - Optional KPI row composed via StatCardRow + StatCard from organisms
 *
 * Token discipline:
 *   - Title text → --color-ink (system)
 *   - Prefix chip background → --color-bg (system; subtle gray)
 *   - StatCard accents inherit per-card brand/system token rule (KPI
 *     row composition delegates to the organism's own discipline)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface EntityHeaderProps {
  prefix?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  statusPills?: ReactNode;
  actions?: ReactNode;
  kpis?: ReactNode;
}

export function EntityHeader({
  prefix,
  title,
  subtitle,
  statusPills,
  actions,
  kpis,
}: EntityHeaderProps) {
  return (
    <header style={{ marginBottom: 'var(--sp-6)' }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-4)',
        marginBottom: kpis ? 'var(--sp-5)' : 0,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {prefix && (
            <div style={{
              display: 'inline-block',
              padding: 'var(--sp-0-5) var(--sp-2)',
              borderRadius: 'var(--r-sm)',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-ink-3)',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-medium)',
              marginBottom: 'var(--sp-2)',
            }}>
              {prefix}
            </div>
          )}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-3)',
            flexWrap: 'wrap',
          }}>
            <h1 style={{
              margin: 0,
              fontSize: 'var(--fs-3xl)',
              fontWeight: 'var(--fw-semibold)',
              letterSpacing: 'var(--ls-tight)',
              lineHeight: 'var(--lh-tight)',
              color: 'var(--color-ink)',
            }}>{title}</h1>
            {statusPills && (
              <div style={{
                display: 'flex',
                gap: 'var(--sp-2)',
                alignItems: 'center',
              }}>{statusPills}</div>
            )}
          </div>
          {subtitle && (
            <div style={{
              marginTop: 'var(--sp-1)',
              color: 'var(--color-ink-3)',
              fontSize: 'var(--fs-md)',
            }}>{subtitle}</div>
          )}
        </div>
        {actions && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            flexShrink: 0,
          }}>{actions}</div>
        )}
      </div>
      {kpis && (
        <StatCardRow>
          {kpis}
        </StatCardRow>
      )}
    </header>
  );
}
